/**
 * Map visual encoding constants — colors and stroke patterns
 * for initiative fill, status indicators, source borders, and node types.
 *
 * Color values match the CSS custom properties defined in index.css @theme.
 */

import type {
  StrategicInitiative,
  DataSource,
} from '../types/adjacency';

// ── Initiative Colors ────────────────────────────────────────────────

/** Circle fill color by strategic initiative — matches Golden Image Platform palette. */
export const INITIATIVE_COLORS: Record<StrategicInitiative, string> = {
  'US Mining':            '#2563eb', // blue
  'Proximity Detection':  '#059669', // green
  'Analytics':            '#d97706', // amber
  'Maritime':             '#7c3aed', // purple
  'International Mining': '#dc2626', // red
  'New Verticals':        '#ec4899', // pink
  'Capabilities':         '#0891b2', // cyan
  'Other':                '#6b7280', // gray
};

/** Dark variant for node fills on dark canvas background. */
export const INITIATIVE_COLORS_DARK: Record<StrategicInitiative, string> = {
  'US Mining':            '#1e40af',
  'Proximity Detection':  '#065f46',
  'Analytics':            '#92400e',
  'Maritime':             '#5b21b6',
  'International Mining': '#991b1b',
  'New Verticals':        '#9d174d',
  'Capabilities':         '#155e75',
  'Other':                '#374151',
};

/** Light background variant for badges and highlights. */
export const INITIATIVE_COLORS_BG: Record<StrategicInitiative, string> = {
  'US Mining':            '#dbeafe',
  'Proximity Detection':  '#d1fae5',
  'Analytics':            '#fef3c7',
  'Maritime':             '#ede9fe',
  'International Mining': '#fee2e2',
  'New Verticals':        '#fce7f3',
  'Capabilities':         '#cffafe',
  'Other':                '#f3f4f6',
};

// ── Status Colors ────────────────────────────────────────────────────

/** Status indicator dot color. */
export const STATUS_COLORS: Record<string, string> = {
  'Complete': '#22c55e',
  'In Progress': '#3b82f6',
  'On Hold': '#f59e0b',
  'Not Started': '#6b7280',
  'Proposed/Pending': '#a855f7',
  'Blocked': '#ef4444',
  'Completed': '#22c55e',
};

/** Fallback color when status is null. */
export const STATUS_DEFAULT_COLOR = '#6b7280';

// ── Source Border Patterns ───────────────────────────────────────────

/** SVG stroke-dasharray for data source differentiation. */
export const SOURCE_DASH_PATTERNS: Record<DataSource, string | undefined> = {
  'priority-list': undefined,   // solid
  'adjacency-map': '4 2',      // dashed
  'manual': '1.5 1.5',      // dotted
};
